import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntityPreview, Home, IntegrationStatus, Me, ModuleStatus, PredicateDefinition, Preferences, RelationDto, EntityTypeDefinition, ExportJob } from '@nw/contracts';
import { api } from './api.js';

export const qk = {
  me: ['me'] as const,
  preferences: ['preferences'] as const,
  registry: ['registry'] as const,
  modules: ['modules'] as const,
  home: ['home'] as const,
  search: (q: string) => ['search', q] as const,
  entity: (key: string) => ['entity', key] as const,
  integrations: ['integrations'] as const,
  diagnostics: ['diagnostics'] as const,
  graph: (params: string) => ['graph', params] as const,
};

export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => (await api<Me>('/me')).data,
    retry: false,
    staleTime: 60_000,
  });
}

export function usePreferences() {
  return useQuery({
    queryKey: qk.preferences,
    queryFn: async () => (await api<Preferences>('/preferences')).data,
    staleTime: 60_000,
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Omit<Preferences, 'revision'>>) => {
      const current = qc.getQueryData<Preferences>(qk.preferences) ?? (await api<Preferences>('/preferences')).data;
      return (
        await api<Preferences>('/preferences', {
          method: 'PATCH',
          body: { ...patch, expectedRevision: current.revision },
        })
      ).data;
    },
    onSuccess: (p) => {
      qc.setQueryData(qk.preferences, p);
      void qc.invalidateQueries({ queryKey: qk.home });
    },
  });
}

export type Registry = {
  entityTypes: Array<EntityTypeDefinition & { moduleId: string }>;
  predicates: Array<PredicateDefinition>;
};
export function useRegistry() {
  return useQuery({
    queryKey: qk.registry,
    queryFn: async () => (await api<Registry>('/registry')).data,
    staleTime: 5 * 60_000,
  });
}

export function useModules() {
  return useQuery({
    queryKey: qk.modules,
    queryFn: async () => (await api<ModuleStatus[]>('/modules')).data,
  });
}

export function useHome() {
  return useQuery({
    queryKey: qk.home,
    queryFn: async () => (await api<Home>('/home')).data,
    // While projections catch up, poll gently so the "updating connections" state clears itself.
    refetchInterval: (q) => ((q.state.data?.pendingProjections ?? 0) > 0 ? 1500 : false),
  });
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: qk.search(q),
    queryFn: async ({ signal }) => {
      const res = await api<EntityPreview[], { nextCursor: string | null }>(`/search?q=${encodeURIComponent(q)}&limit=24`, { signal });
      return { items: res.data, nextCursor: res.meta?.nextCursor ?? null };
    },
    enabled: q.trim().length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export type EntityDetail = {
  entity: EntityPreview;
  relations: RelationDto[];
  related: Record<string, EntityPreview>;
};
export function useEntity(key: string | null) {
  return useQuery({
    queryKey: qk.entity(key ?? ''),
    queryFn: async () => (await api<EntityDetail>(`/entities/${encodeURIComponent(key!)}`)).data,
    enabled: !!key,
    retry: false,
  });
}

export function useIntegrations() {
  return useQuery({
    queryKey: qk.integrations,
    queryFn: async () => (await api<IntegrationStatus[]>('/integrations')).data,
  });
}

export function useRequestExport() {
  return useMutation({
    mutationFn: async () => (await api<ExportJob>('/exports', { method: 'POST' })).data,
  });
}

export function useExportStatus(id: string | null) {
  return useQuery({
    queryKey: ['export', id],
    queryFn: async () => (await api<ExportJob>(`/exports/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && ['queued', 'running'].includes(q.state.data.status) ? 1000 : false),
  });
}

/** Invoke a browser-allowed public capability on behalf of a declared caller module. */
export function invokeCapability<T>(callerModuleId: string, capabilityId: string, input: unknown) {
  return api<T>(`/capabilities/${encodeURIComponent(capabilityId)}`, {
    method: 'POST',
    body: { callerModuleId, input },
  }).then((r) => r.data);
}
